import type { BulkMappingRow, MasterAdAccount, MasterPage } from '../types/bulkOnboarding.js';

// Pure fuzzy-matching for the bulk-onboarding mapping builder. Shared by the
// server (builds the initial suggestions) and unit-tested directly. No I/O.
//
// Snap naming convention means a club's Facebook Page is usually named almost
// identically to the kyck account ("Snap Fitness Perth City"), so name-match
// hit rate is high. Ad accounts, by contrast, are frequently named opaquely
// ("Account 1043…"), so we hold a HIGH confidence bar for them and default to
// "— select —" rather than auto-guessing the wrong ad account (the expensive
// footgun).

// A page at >= this score is auto-filled as the suggestion.
export const PAGE_CONFIDENT = 0.6;
// An ad account must clear this HIGHER bar to be auto-filled; below it we leave
// the row's ad account null (AM picks manually / via CSV).
export const AD_ACCOUNT_CONFIDENT = 0.85;
// When the top two page candidates score within this margin the match is
// AMBIGUOUS (common now that Location Pages share a bare brand name) — we still
// fill the best guess but flag needsReview so the AM looks rather than trusting
// a silent pick.
export const PAGE_AMBIGUOUS_MARGIN = 0.1;

export function normalizeForMatch(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function tokens(s: string): string[] {
  return s ? s.split(' ').filter(Boolean) : [];
}

// 0..1 similarity. Exact normalized match = 1; the CANDIDATE containing the
// full query = 0.9 (the candidate carries every query token, incl. any location
// qualifier); otherwise Jaccard token overlap. Empty inputs score 0.
//
// DIRECTIONAL on purpose: the reverse — query containing a shorter candidate —
// is NOT a flat 0.9. A bare-brand candidate ("Fitstop") sitting inside a
// located query ("Fitstop Biloela") would otherwise score 0.9 and let the
// head-office page beat every per-location page. Falling through to Jaccard
// scores it by real token overlap instead (so it loses to the location page
// whose handle carries the suburb).
export function scoreMatch(query: string, candidate: string): number {
  const q = normalizeForMatch(query);
  const c = normalizeForMatch(candidate);
  if (!q || !c) return 0;
  if (q === c) return 1;
  if (c.includes(q)) return 0.9;
  const qt = tokens(q);
  const ct = new Set(tokens(c));
  if (qt.length === 0) return 0;
  const inter = qt.filter((t) => ct.has(t)).length;
  if (inter === 0) return 0;
  const union = new Set([...qt, ...tokens(c)]).size;
  return inter / union;
}

export type Match<T> = { item: T; score: number };

// Best-scoring candidate for a query, or null when the pool is empty / nothing
// scores above zero. Ties resolve to the first candidate (stable).
export function bestMatch<T>(
  query: string,
  candidates: T[],
  key: (c: T) => string,
): Match<T> | null {
  let best: Match<T> | null = null;
  for (const item of candidates) {
    const score = scoreMatch(query, key(item));
    if (best === null || score > best.score) best = { item, score };
  }
  return best && best.score > 0 ? best : null;
}

// A Facebook username/handle, normalized to align with account-name tokens:
// strip a leading '@' and split camelCase ("AnytimeFitnessAldinga" →
// "anytime fitness aldinga") so the location qualifier in the handle matches.
export function normalizeUsername(username: string | null | undefined): string {
  if (!username) return '';
  const spaced = username.replace(/^@+/, '').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return normalizeForMatch(spaced);
}

// Page match score = account name vs the COMBINED "name + handle" candidate, so
// a Location Page whose bare name is the brand but whose handle carries the
// suburb scores on the suburb. (Plain name-only scoring gives every same-brand
// page the same containment score and picks an arbitrary one.)
export function pageScore(accountName: string, page: MasterPage): number {
  const candidate = `${page.name} ${normalizeUsername(page.username)}`.trim();
  return scoreMatch(accountName, candidate);
}

// Best page match for a club, with ambiguity detection: returns the top match
// plus whether the runner-up is within PAGE_AMBIGUOUS_MARGIN (a near-tie the AM
// must adjudicate). null when the pool is empty / nothing scores.
export function bestPageMatch(
  accountName: string,
  pages: MasterPage[],
): { item: MasterPage; score: number; ambiguous: boolean } | null {
  const scored = pages
    .map((item) => ({ item, score: pageScore(accountName, item) }))
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score);
  if (scored.length === 0) return null;
  const top = scored[0]!;
  const second = scored[1];
  const ambiguous = !!second && top.score - second.score < PAGE_AMBIGUOUS_MARGIN;
  return { item: top.item, score: top.score, ambiguous };
}

// Build one mapping row for an un-onboarded club: fuzzy-suggest a Page (auto-
// filled at PAGE_CONFIDENT) and an ad account (auto-filled only at the higher
// AD_ACCOUNT_CONFIDENT — otherwise left null). `needsReview` flags any row
// where either suggestion wasn't confident.
export function buildMappingRow(
  account: { id: string; name: string; fbAdAccountId?: string | null },
  pages: MasterPage[],
  adAccounts: MasterAdAccount[],
): BulkMappingRow {
  // Match on name + handle (Location Pages carry the suburb in the handle), and
  // flag near-ties as ambiguous so same-brand collisions aren't picked silently.
  const pageMatch = bestPageMatch(account.name, pages);
  const pageConfident = !!pageMatch && pageMatch.score >= PAGE_CONFIDENT;
  const pageAmbiguous = !!pageMatch && pageMatch.ambiguous;
  const pageId = pageConfident ? pageMatch!.item.id : null;
  const pageName = pageConfident ? pageMatch!.item.name : null;
  const pageUsername = pageConfident ? pageMatch!.item.username : null;

  // Ad account: the kyck Account.fbAdAccountId is the SINGLE source of truth, so
  // when it's set we pre-fill from it (confidence 1, stored RAW exactly as kyck
  // holds it — toZernioAdAccountId prefixes at the connectAds boundary). Only
  // when it's empty do we fall back to the high-bar fuzzy match; below the bar
  // stays null ("— select —"), never auto-guessed. The AM still reviews/confirms.
  const accountAdId = (account.fbAdAccountId ?? '').trim();
  let adAccountId: string | null;
  let adAccountName: string | null;
  let adAccountConfidence: number;
  let adAccountSource: BulkMappingRow['adAccountSource'];
  let adConfident: boolean;

  if (accountAdId !== '') {
    adAccountId = accountAdId;
    // Show the name when it's already in hand from the master list (no extra
    // Zernio call); otherwise id-only. Master ids are act_-prefixed, kyck holds
    // raw — compare both ways.
    adAccountName = adAccountNameFromMaster(accountAdId, adAccounts);
    adAccountConfidence = 1;
    adAccountSource = 'account';
    adConfident = true;
  } else {
    const adMatch = bestMatch(account.name, adAccounts, (a) => a.name);
    adConfident = !!adMatch && adMatch.score >= AD_ACCOUNT_CONFIDENT;
    adAccountId = adConfident ? adMatch!.item.id : null;
    adAccountName = adConfident ? adMatch!.item.name : null;
    adAccountConfidence = adMatch ? round(adMatch.score) : 0;
    adAccountSource = adConfident ? 'match' : null;
  }

  const ready = pageId !== null && adAccountId !== null;
  return {
    accountId: account.id,
    accountName: account.name,
    profileName: account.name,
    pageId,
    pageName,
    pageUsername,
    pageConfidence: pageMatch ? round(pageMatch.score) : 0,
    adAccountId,
    adAccountName,
    adAccountConfidence,
    adAccountSource,
    // Ambiguous (near-tie) page matches force a look even though a guess is filled.
    needsReview: !pageConfident || pageAmbiguous || !adConfident,
    ready,
    // A freshly built/matched row is NEVER auto-confirmed — the AM confirms it
    // (per-row or "Confirm all matched") before it can run.
    confirmed: false,
  };
}

// Resolve an ad account's display name from the already-fetched master list by
// id, tolerating the raw/act_ mismatch. Returns null when not present (no
// per-row Zernio lookup — that would be 500 calls).
function adAccountNameFromMaster(rawId: string, adAccounts: MasterAdAccount[]): string | null {
  const strip = (s: string) => s.replace(/^act_/, '');
  const target = strip(rawId);
  const hit = adAccounts.find((a) => strip(a.id) === target);
  return hit?.name ?? null;
}

function round(n: number): number {
  return Math.round(n * 100) / 100;
}

// Recompute the derived `ready`/`needsReview` flags after a row changes.
// Preserves `confirmed` — callers decide when to drop confirmation (a
// page/ad-account edit drops it; a profile-name edit doesn't).
export function recomputeRowFlags(row: BulkMappingRow): BulkMappingRow {
  const ready = !!row.pageId && !!row.adAccountId;
  return { ...row, ready, needsReview: !ready };
}
